import classNames from 'classnames';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import {
  ActionMeta,
  GroupBase,
  default as MultiReactSelect,
  OnChangeValue,
  StylesConfig,
} from 'react-select';
import { OptionStringType } from '~/features/vault/profile/types';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import type { FormTypography } from '../FormLabel';
import { getFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';
import { FormSelectInput, SelectStyles } from '../Select/Select';

export const MULTISELECT_STYLES: StylesConfig<OptionStringType, true> = {
  multiValueRemove: (base, state) => {
    return state.data.isFixed ? { ...base, display: 'none' } : base;
  },
  singleValue: (base) => ({
    ...base,
    overflow: 'visible',
  }),
};

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  typography?: FormTypography;
  value?: readonly OptionStringType[];
  options: readonly (PathValue<TFieldValues, TName> | GroupBase<PathValue<TFieldValues, TName>>)[];
  isClearable?: boolean;
  /** Passed through to react-select only when defined; otherwise react-select's default applies. */
  placeholder?: string;
  /** Passed through to react-select only when defined; otherwise react-select's default applies. */
  closeMenuOnSelect?: boolean;
  /** Passed through to react-select only when defined; otherwise react-select's default applies. */
  blurInputOnSelect?: boolean;
  onChange?: (
    newValue: OnChangeValue<OptionStringType, true>,
    actionMeta: ActionMeta<OptionStringType>
  ) => void;
  styles?: StylesConfig<OptionStringType, true>;
  useShortValue?: boolean;
}
export const Multiselect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    cyTag,
    label,
    options,
    onChange,
    flagged,
    required,
    tooltip,
    errorCyTag,
    reserveFeedbackSpace,
    disabled,
    isClearable = false,
    placeholder,
    closeMenuOnSelect,
    blurInputOnSelect,
    styles = MULTISELECT_STYLES,
    value: controlledValue,
    typography = 'default',
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const error = getFieldError(errors, name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const resolvedErrorCyTag = errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        const selectValue = controlledValue ?? field.value;
        const hasValue = Array.isArray(selectValue) ? selectValue.length > 0 : !!selectValue;
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            typography={typography}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={resolvedErrorCyTag}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <MultiReactSelect
                {...field}
                inputId={name}
                value={hasValue ? selectValue : null}
                unstyled
                isMulti
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-errormessage={error?.message ? errorId : undefined}
                aria-describedby={error?.message ? errorId : description ? helperId : undefined}
                components={{ Input: FormSelectInput }}
                options={options}
                className="basic-multi-select"
                classNamePrefix="select"
                isClearable={isClearable}
                {...(placeholder !== undefined ? { placeholder } : {})}
                {...(closeMenuOnSelect !== undefined ? { closeMenuOnSelect } : {})}
                {...(blurInputOnSelect !== undefined ? { blurInputOnSelect } : {})}
                isDisabled={disabled}
                formatOptionLabel={(option, meta) => {
                  if (meta.context === 'value' && props.useShortValue) return option.value;
                  return option.label;
                }}
                onChange={(option, actionMeta) => {
                  field.onChange(option);
                  onChange?.(option, actionMeta);
                }}
                styles={
                  typography === 'comfortable'
                    ? {
                        ...styles,
                        multiValueLabel: (base, state) => ({
                          ...(styles.multiValueLabel?.(base, state) ?? base),
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        }),
                      }
                    : styles
                }
                classNames={{
                  ...SelectStyles(flagged, !!error, typography),
                  multiValue: () => classNames('px-2 bg-white rounded-md mx-1 border-1'),
                  multiValueRemove: () =>
                    classNames(
                      'px-1 rounded-r-md hover:bg-grey-900 hover:text-error-50 cursor-pointer'
                    ),
                  multiValueLabel: () =>
                    classNames(
                      'px-1 py-1 border-primary-700',
                      typography === 'comfortable'
                        ? 'text-[1rem] font-normal leading-normal text-content-primary'
                        : 'body-text'
                    ),
                }}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
